import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, AlertTriangle, Crown } from "lucide-react";

export default function AdminLogin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState(auth.getUser());

  // If user not logged via Discord, redirect to landing with discord login
  useEffect(() => {
    if (!auth.getToken()) {
      toast.error("Please log in with Discord first.");
      const t = setTimeout(() => navigate("/"), 1200);
      return () => clearTimeout(t);
    }
    api.get("/auth/me").then((r) => setUser(r.data.user)).catch(() => {
      auth.clear();
      navigate("/");
    });
  }, [navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!password.trim()) {
      toast.error("Enter the admin password.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/login", {
        path_token: token,
        password,
      });
      toast.success("Admin access granted.");
      // refresh sidebar/admin gating
      setTimeout(() => navigate("/dashboard/admin"), 600);
    } catch (err) {
      const code = err.response?.status;
      if (code === 401) toast.error("Invalid credentials.");
      else if (code === 403) toast.error("Admin login requires a real Discord account.");
      else if (code === 503) toast.error("Admin login is not configured.");
      else toast.error("Login failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!auth.getToken()) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
          <p className="text-zinc-400">Redirecting to Discord login…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 tactical-grid-fine" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Crown className="w-10 h-10 text-brand-orange mx-auto mb-2" />
          <div className="overline mb-1">// CLASSIFIED ACCESS</div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Admin Vault</h1>
          <p className="text-sm text-zinc-500 mt-2">
            Two-factor authentication required. Your activity is logged.
          </p>
        </div>

        <div className="tactical-card p-6 space-y-4">
          {user && (
            <div className="flex items-center gap-3 pb-4 border-b border-brand-line">
              {user.avatar
                ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-md object-cover" />
                : <div className="w-10 h-10 rounded-md bg-brand-orange/20 border border-brand-orange flex items-center justify-center font-bold text-brand-orange uppercase">{user.username?.[0]}</div>
              }
              <div className="min-w-0">
                <div className="font-semibold truncate">{user.username}</div>
                <div className="font-mono-tabular text-[10px] text-zinc-500 uppercase flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                  Discord OAuth verified
                </div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="overline mb-1.5 block">Admin Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="password"
                  autoComplete="off"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full bg-brand-elevated border border-brand-line rounded-lg pl-9 pr-3 py-2.5 text-sm font-mono-tabular focus:border-brand-orange focus:outline-none"
                  data-testid="admin-login-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
              data-testid="admin-login-submit"
            >
              {busy ? "Authenticating…" : "Unlock Admin Panel"}
            </button>
          </form>

          <div className="text-[11px] font-mono-tabular text-zinc-600 pt-3 border-t border-brand-line text-center">
            URL signature verified · failed attempts are logged
          </div>
        </div>
      </div>
    </div>
  );
}
